import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Box, Typography, Button, Alert, Paper } from '@mui/material';
import AppointmentService from '../services/AppointmentService';

const AuthTest = () => {
  const { isAuthenticated, currentUser, login } = useAuth();
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addResult = (test, success, message) => {
    setTestResults(prev => [...prev, { test, success, message, timestamp: new Date().toLocaleTimeString() }]);
  };

  const testAuthentication = async () => {
    setLoading(true);
    setTestResults([]);

    // Test 1: Check if user is authenticated
    addResult('Authentication Status', isAuthenticated, 
      isAuthenticated ? `User authenticated: ${currentUser?.username}` : 'User not authenticated');

    // Test 2: Check if token exists in localStorage
    const token = localStorage.getItem('token');
    addResult('Token in localStorage', !!token, 
      token ? 'Token found in localStorage' : 'No token in localStorage');

    // Test 3: Try to fetch appointments
    if (isAuthenticated && token) {
      try {
        const appointments = await AppointmentService.getAllAppointments();
        addResult('Appointment API Call', true, 
          `Successfully fetched ${appointments.data?.length || 0} appointments`);
      } catch (error) {
        addResult('Appointment API Call', false, 
          `Failed to fetch appointments: ${error.message}`);
        
        // Additional error details
        if (error.response) {
          addResult('API Error Details', false, 
            `Status: ${error.response.status}, Message: ${error.response.data?.message || 'Unknown error'}`);
        }
      }
    } else {
      addResult('Appointment API Call', false, 'Skipped - user not authenticated');
    }

    setLoading(false);
  };

  const testLogin = async () => {
    try {
      setLoading(true);
      // Test with default credentials - you may need to adjust these
      await login('admin', 'admin123'); // Adjust credentials as needed
      addResult('Test Login', true, 'Login successful');
    } catch (error) {
      addResult('Test Login', false, `Login failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Run initial test when component mounts
    testAuthentication();
  }, [isAuthenticated]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Authentication & API Test
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Button 
          variant="contained" 
          onClick={testAuthentication} 
          disabled={loading}
          sx={{ mr: 2 }}
        >
          Test Authentication & API
        </Button>
        
        {!isAuthenticated && (
          <Button 
            variant="outlined" 
            onClick={testLogin} 
            disabled={loading}
          >
            Test Login
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Test Results:
        </Typography>
        
        {testResults.length === 0 && !loading && (
          <Typography color="text.secondary">
            No tests run yet. Click "Test Authentication & API" to start.
          </Typography>
        )}
        
        {testResults.map((result, index) => (
          <Alert 
            key={index} 
            severity={result.success ? 'success' : 'error'} 
            sx={{ mb: 1 }}
          >
            <strong>{result.test}:</strong> {result.message} 
            <Typography variant="caption" display="block">
              {result.timestamp}
            </Typography>
          </Alert>
        ))}
        
        {loading && (
          <Alert severity="info">
            Running tests...
          </Alert>
        )}
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Current State:
        </Typography>
        <Typography variant="body2">
          <strong>Authenticated:</strong> {isAuthenticated ? 'Yes' : 'No'}<br/>
          <strong>User:</strong> {currentUser?.username || 'None'}<br/>
          <strong>Roles:</strong> {currentUser?.roles?.join(', ') || 'None'}<br/>
          <strong>Token:</strong> {localStorage.getItem('token') ? 'Present' : 'Missing'}
        </Typography>
      </Box>
    </Box>
  );
};

export default AuthTest;